import { z } from "zod";
import {
  ANOMALY_KIND_BEHAVIOR,
  ANOMALY_REASON,
  COOLING_THRESHOLD_MINOR_KEY as DOMAIN_COOLING_THRESHOLD_KEY,
  COOLING_WINDOW_MINUTES_KEY as DOMAIN_COOLING_WINDOW_KEY,
  COOLING_WINDOW_MAX_MINUTES,
  isCoolingThresholdMinor,
} from "@goppy/domain";
import { EXPENSE_STATUS, expenseStatusSchema } from "./expenses.js";

/**
 * Contract US-E19.F2-0201-S02 (@UC9299 @UC9300 @UC9301 @UC9302) — COOLING-PERIOD · FUND-FREEZE ·
 * ANOMALY HÀNH VI. 🔴 @gate money + config.
 *
 * (a) Config `cooling_threshold_minor` (**CHUỖI BigInt minor** — B1 no-float, KHÔNG `z.number()`) +
 *     `cooling_window_minutes`. Key + type-guard DERIVE từ `@goppy/domain` (1 nguồn sự thật).
 * (b) `FUND_FROZEN_CODE` — mã 409 khi quỹ bị đóng băng (mirror `GROUP_SUSPENDED_CODE` cấp GROUP).
 * (c) `anomalyAlertSchema` — `kind` literal `'behavior'` (KHÁC anomaly KẾ TOÁN/drift); `detail`
 *     `.strict()` NO-PII / NO-số-tiền-thô (chỉ reasonCode + giờ + hệ số bội).
 * (d) `coolingScheduledResponseSchema` — kết quả duyệt chi lớn: **status `cooling`, KHÔNG có
 *     `ledgerTxId`** (0 bút toán tại thời điểm duyệt — bất biến ép Ở CONTRACT: schema `.strict()`
 *     KHÔNG có field ledgerTxId ⇒ không thể trả txId ở nhánh hoãn).
 */

/* ─────────────────────────── (a) CONFIG cooling ─────────────────────────── */

export const COOLING_THRESHOLD_MINOR_KEY = DOMAIN_COOLING_THRESHOLD_KEY;
export const COOLING_WINDOW_MINUTES_KEY = DOMAIN_COOLING_WINDOW_KEY;

/** Thứ tự ổn định cho UI/seed/test. */
export const COOLING_CONFIG_KEY_LIST = [
  COOLING_THRESHOLD_MINOR_KEY,
  COOLING_WINDOW_MINUTES_KEY,
] as const;

/** Ngưỡng cooling = chuỗi BigInt minor ≥ 0 (guard domain — no-float, KHÔNG z.number). */
export const coolingThresholdMinorPatchSchema = z
  .string()
  .refine((v) => isCoolingThresholdMinor(v), {
    message: "Ngưỡng hoãn chi phải là số nguyên không âm (đơn vị minor, dạng chuỗi).",
  });

export const coolingWindowMinutesPatchSchema = z
  .number()
  .int()
  .min(1)
  .max(COOLING_WINDOW_MAX_MINUTES);

/** Config cooling hiệu lực của 1 quỹ (fund > group > system). Tiền = string BigInt. */
export const coolingConfigSchema = z
  .object({
    coolingThresholdMinor: coolingThresholdMinorPatchSchema,
    coolingWindowMinutes: coolingWindowMinutesPatchSchema,
  })
  .strict();

export type CoolingConfigResponse = z.infer<typeof coolingConfigSchema>;

/* ─────────────────────────── (b) FUND_FROZEN 409 ────────────────────────── */

/**
 * 🔴 Mã lỗi 409 khi quỹ bị ĐÓNG BĂNG (nghi tài khoản bị chiếm — SEC-ACCOUNT-HACK).
 * Fail-closed: MỌI WRITE tiền (post chi / duyệt chi / confirm payment / auto-post cooling) → 409,
 * 0 bút toán mới. READ (xem số dư/lịch sử minh bạch) KHÔNG bị chặn.
 * Mirror `GROUP_SUSPENDED_CODE` (cấp GROUP) — cùng pattern, khác tầng.
 */
export const FUND_FROZEN_CODE = "FUND_FROZEN" as const;

/**
 * FREEZE-EXEMPT-HARDEN LOW-1 — 404 mã ổn định của chốt ghi-trong-tx (`assertFundWritableInTx`): quỹ của bút toán không còn (vd bị xoá giữa guard và
 * tx). Trước: 404 trần không `code` ⇒ client không phân biệt được với "không có quyền / không tồn tại" của guard. Không `params`.
 */
export const FUND_NOT_FOUND_CODE = "FUND_NOT_FOUND" as const;

/** Trạng thái quỹ (khớp CHECK db `funds_status_check`). */
export const FUND_STATUS = {
  ACTIVE: "active",
  FROZEN: "frozen",
} as const;
export type FundStatus = (typeof FUND_STATUS)[keyof typeof FUND_STATUS];

export const fundStatusSchema = z.enum([FUND_STATUS.ACTIVE, FUND_STATUS.FROZEN]);

/* ────────────────────── (c) AnomalyAlert (hành vi) ──────────────────────── */

/** Loại cảnh báo — CHỈ 'behavior'. Anomaly KẾ TOÁN (drift) là luồng/bảng KHÁC. */
export const ANOMALY_ALERT_KIND_BEHAVIOR = ANOMALY_KIND_BEHAVIOR;

export const anomalyReasonCodeSchema = z.enum([
  ANOMALY_REASON.ODD_HOUR,
  ANOMALY_REASON.MULTIPLE_OF_MEDIAN,
]);

/**
 * 🔴 `detail` NO-PII / NO-số-tiền-thô. `.strict()` ⇒ field lạ (vd `amountMinor`, `memberName`) LÀM
 * PARSE FAIL ⇒ không thể rò rỉ số tiền/tên người vào cảnh báo out-of-band (email/push).
 */
export const anomalyAlertDetailSchema = z
  .object({
    reasonCode: anomalyReasonCodeSchema,
    hourLocal: z.number().int().min(0).max(23).optional(),
    /** Hệ số bội so median 30 ngày (làm tròn xuống) — KHÔNG phải số tiền. */
    multipleOfMedian: z.number().int().nonnegative().optional(),
  })
  .strict();

export type AnomalyAlertDetail = z.infer<typeof anomalyAlertDetailSchema>;

export const anomalyAlertSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    expenseId: z.string().uuid().nullable(),
    kind: z.literal(ANOMALY_ALERT_KIND_BEHAVIOR),
    detail: anomalyAlertDetailSchema,
    createdAt: z.string().datetime(),
  })
  .strict();

export type AnomalyAlert = z.infer<typeof anomalyAlertSchema>;

/* ───────────────── (d) duyệt chi lớn ⇒ HOÃN (cooling) ───────────────────── */

/**
 * 🔴 US-E24.F7-0356-S01 (@UC9587): THAM CHIẾU LẠI `EXPENSE_STATUS` — **không giữ chuỗi rời**.
 * Trước đây hai hằng này là literal độc lập, nằm ngoài `expenseStatusSchema` ⇒ drift 6-vs-8 giữa
 * contract và CHECK của DB ⇒ 500 ở trang admin khi có khoản chi đang nguội. Một nguồn sự thật.
 */
export const COOLING_STATUS = EXPENSE_STATUS.COOLING;
export const EXPENSE_CANCELLED_STATUS = EXPENSE_STATUS.CANCELLED;

/**
 * 🔴 BẤT BIẾN Ở CONTRACT: nhánh cooling **KHÔNG CÓ** `ledgerTxId` — schema `.strict()` ⇒ nếu ai đó
 * lỡ post ledger rồi trả txId ở nhánh hoãn, parse FAIL ngay (fail-loud). "Hoãn" = 0 bút toán.
 */
export const coolingScheduledResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    amountMinor: z.string().regex(/^\d+$/),
    currency: z.string().length(3),
    newStatus: z.literal(COOLING_STATUS),
    /** Mốc worker auto-post (nếu quỹ không bị freeze và khoản chi không bị huỷ). */
    postAfter: z.string().datetime(),
    coolingThresholdMinor: z.string().regex(/^\d+$/),
  })
  .strict();

export type CoolingScheduledResponse = z.infer<typeof coolingScheduledResponseSchema>;

/** Huỷ trong cửa sổ nguội ⇒ `cancelled`. KHÔNG post, KHÔNG bút toán mồ côi. */
export const cancelCoolingResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    newStatus: z.literal(EXPENSE_CANCELLED_STATUS),
    cancelledAt: z.string().datetime(),
  })
  .strict();

export type CancelCoolingResponse = z.infer<typeof cancelCoolingResponseSchema>;

/** Message lỗi biên (khớp toast mobile). */
export const COOLING_NOT_PENDING_MESSAGE =
  "Khoản chi không ở trạng thái chờ ghi sổ (cooling).";
export const FUND_FROZEN_MESSAGE =
  "Quỹ đang bị đóng băng — mọi thay đổi tiền tạm khoá.";

/**
 * 🔴 US-E24.F7-0356-S01 (@UC9587) — **ALIAS, KHÔNG PHẢI WHITELIST THỨ HAI.**
 *
 * US-AE16.F1-0369 dựng danh sách 8 giá trị ở đây vì lúc đó `expenseStatusSchema` (lane client) mới
 * có 6 và việc mở rộng nó được hoãn sang task này. Nay `EXPENSE_STATUS` **đã là 8** ⇒ giữ một danh
 * sách liệt-kê-tay song song chính là cái drift mà cả hai task muốn diệt. Hằng này nay **DẪN XUẤT**
 * từ `expenseStatusSchema.options`; tên cũ giữ lại cho call-site admin (`ExpensesPage.tsx`, `api.ts`).
 *
 * ⛔ CẤM khai enum thứ ba (kiểu `adminExpenseStatusSchema` liệt kê tay ở module admin) — đó chính là
 * cách bug 500-vĩnh-viễn sinh ra: `map-expense.parse` ném ZodError trên mọi dòng `cooling`/`cancelled`.
 * Cùng họ drift 2 whitelist txType (CLAUDE.md §3.8). Guard tự-khám-phá: `expense-status-whitelist.test.ts`.
 */
export const EXPENSE_STATUS_WITH_COOLING = expenseStatusSchema.options;

/** Schema zod cho tập trên — cùng một nguồn với `expenseStatusSchema`. */
export const expenseStatusWithCoolingSchema = expenseStatusSchema;

export type ExpenseStatusWithCooling = z.infer<typeof expenseStatusWithCoolingSchema>;
